import { CommonModule } from '@angular/common'
import { Component, ElementRef, ViewChild, output, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { InternationalizationService } from '../../../../services/internationalization.service'
import { MediaPickerComponent } from './media-picker/media-picker.component'

@Component({
  selector: 'app-chat-input',
  standalone: true,
  imports: [CommonModule, FormsModule, MediaPickerComponent],
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.scss',
})
export class ChatInputComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLInputElement>

  constructor(protected translateService: InternationalizationService) {}

  readonly sendMessage = output<{ content: string }>()
  readonly sendFile = output<{ file: File; content?: string }>()
  readonly typing = output<{ hasText: boolean }>()

  readonly messageText = signal('')
  readonly selectedFile = signal<File | null>(null)
  readonly filePreviewUrl = signal<string | null>(null)
  readonly mediaPickerOpen = signal(false)

  onSend(): void {
    const text = this.messageText().trim()
    const file = this.selectedFile()

    if (file) {
      this.sendFile.emit({ file, content: text || undefined })
      this.clearFile()
      this.messageText.set('')
      this.typing.emit({ hasText: false })
      return
    }

    if (text) {
      this.sendMessage.emit({ content: text })
      this.messageText.set('')
      this.typing.emit({ hasText: false })
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.onSend()
    }
  }

  onInput(): void {
    this.typing.emit({ hasText: this.messageText().trim().length > 0 })
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files?.length) {
      const file = input.files[0]
      this.selectedFile.set(file)

      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = () => this.filePreviewUrl.set(reader.result as string)
        reader.readAsDataURL(file)
      } else {
        this.filePreviewUrl.set(null)
      }
    }
  }

  openFilePicker(): void {
    this.fileInput.nativeElement.click()
  }

  clearFile(): void {
    this.selectedFile.set(null)
    this.filePreviewUrl.set(null)
    if (this.fileInput) {
      this.fileInput.nativeElement.value = ''
    }
  }

  toggleMediaPicker(): void {
    this.mediaPickerOpen.update((open) => !open)
  }

  closeMediaPicker(): void {
    this.mediaPickerOpen.set(false)
  }

  onEmojiSelected(emoji: string): void {
    const el = this.messageInput?.nativeElement
    const current = this.messageText()

    if (!el) {
      this.messageText.set(current + emoji)
      this.typing.emit({ hasText: this.messageText().trim().length > 0 })
      return
    }

    const start = el.selectionStart ?? current.length
    const end = el.selectionEnd ?? current.length
    const next = current.slice(0, start) + emoji + current.slice(end)
    this.messageText.set(next)
    this.typing.emit({ hasText: next.trim().length > 0 })

    queueMicrotask(() => {
      const pos = start + emoji.length
      el.focus()
      el.setSelectionRange(pos, pos)
    })
  }

  onMediaSelected(event: { file: File; content?: string }): void {
    this.sendFile.emit(event)
    this.mediaPickerOpen.set(false)
  }
}
